import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../lib/api';
import { formatRelative } from '../lib/format';
import { invalidateInventory } from '../lib/hooks';
import { modelLabel } from '../lib/parts';
import type { Part, WorkOrderParts } from '../lib/types';
import { PartKindBadge, StockBadge } from './part-badges';
import { PartFormModal } from './PartFormModal';
import { useToast } from './toast';
import { Alert, Badge, Button, ErrorState, Input, Modal, PageLoader } from './ui';

const sectionTitle = 'mb-1 text-xs font-semibold tracking-wide text-slate-500 uppercase';

function PartRow({ part, canUse, using, onUse }: { part: Part; canUse: boolean; using: boolean; onUse(quantity: number): void }) {
  const [quantity, setQuantity] = useState('1');
  const count = Number(quantity);
  const valid = Number.isInteger(count) && count >= 1 && count <= part.quantityOnHand;
  const location = part.room ? `${part.room.name} · ${part.building?.name ?? ''}` : part.building?.name;

  return (
    <li className="flex flex-wrap items-center gap-3 py-3">
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-900">
          <Link to={`/parts/${part.id}`} className="hover:text-brand-700">
            {part.name}
          </Link>
          <PartKindBadge kind={part.kind} />
        </p>
        <p className="text-xs text-slate-500">{[part.partNumber, location].filter(Boolean).join(' · ') || ' '}</p>
      </div>
      <StockBadge part={part} />
      {canUse && (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            inputMode="numeric"
            min={1}
            max={part.quantityOnHand}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            disabled={part.quantityOnHand === 0}
            className="w-16"
            aria-label={`How many ${part.name}`}
          />
          <Button size="sm" variant="primary" disabled={!valid} loading={using} onClick={() => onUse(count)}>
            Use
          </Button>
        </div>
      )}
    </li>
  );
}

/** Parts that fit a work order's device, and the parts already used on it. */
export function WorkOrderPartsModal({
  open,
  onClose,
  workOrderId,
  canUse,
}: {
  open: boolean;
  onClose(): void;
  workOrderId: string;
  canUse: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const query = useQuery({
    queryKey: ['work-order-parts', workOrderId],
    queryFn: () => api.get<WorkOrderParts>(`/work-orders/${workOrderId}/parts`),
    enabled: open,
  });

  const use = useMutation({
    mutationFn: ({ partId, quantity }: { partId: string; quantity: number; name: string }) =>
      api.post(`/work-orders/${workOrderId}/parts`, { partId, quantity }),
    onSuccess: (_result, { quantity, name }) => {
      invalidateInventory(queryClient);
      toast.success(`Used ${quantity} × ${name}`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const putBack = useMutation({
    mutationFn: (transactionId: string) => api.post(`/work-orders/${workOrderId}/parts/${transactionId}/put-back`),
    onSuccess: () => {
      invalidateInventory(queryClient);
      toast.success('Put back in stock');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const data = query.data;
  const model =
    data?.asset.model && data.asset.manufacturer
      ? modelLabel({ name: data.asset.model, manufacturer: { name: data.asset.manufacturer } })
      : (data?.asset.model ?? data?.asset.manufacturer);

  const renderRows = (parts: Part[]) => (
    <ul className="divide-y divide-slate-100">
      {parts.map((part) => (
        <PartRow
          key={part.id}
          part={part}
          canUse={canUse}
          using={use.isPending && use.variables?.partId === part.id}
          onUse={(quantity) => use.mutate({ partId: part.id, quantity, name: part.name })}
        />
      ))}
    </ul>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Parts for this device"
      description={data && `${data.asset.assetTag} · ${model || data.asset.name}`}
      footer={
        <>
          {canUse && (
            <Button icon={<Plus />} onClick={() => setAdding(true)} disabled={!data}>
              Add a part
            </Button>
          )}
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </>
      }
    >
      {query.isLoading ? (
        <PageLoader />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : (
        data && (
          <div className="space-y-6">
            {!data.asset.modelId && (
              <Alert tone="amber" title="This device doesn’t have a model">
                Only parts that fit any device are listed. Set the manufacturer and model on{' '}
                <Link to={`/assets/${data.asset.id}`} className="font-medium underline">
                  {data.asset.assetTag}
                </Link>{' '}
                to see parts made for it.
              </Alert>
            )}

            {data.used.length > 0 && (
              <section>
                <h3 className={sectionTitle}>Used on this ticket</h3>
                <ul className="divide-y divide-slate-100">
                  {data.used.map((usage) => (
                    <li key={usage.id} className="flex flex-wrap items-center gap-3 py-2.5 text-sm">
                      <div className="min-w-0 flex-1">
                        <p className={usage.reversedAt ? 'text-slate-500 line-through' : 'font-medium text-slate-900'}>
                          {-usage.quantityChange} × {usage.part?.name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {usage.actorName} · {formatRelative(usage.createdAt)}
                          {usage.note && ` · ${usage.note}`}
                        </p>
                      </div>
                      {usage.reversedAt ? (
                        <Badge>Put back</Badge>
                      ) : (
                        canUse && (
                          <Button
                            size="sm"
                            icon={<Undo2 />}
                            loading={putBack.isPending && putBack.variables === usage.id}
                            onClick={() => putBack.mutate(usage.id)}
                          >
                            Put back
                          </Button>
                        )
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {data.asset.modelId && (
              <section>
                <h3 className={sectionTitle}>Made for {model}</h3>
                {data.compatible.length === 0 ? (
                  <p className="py-2 text-sm text-slate-500">No parts on file for this model yet.</p>
                ) : (
                  renderRows(data.compatible)
                )}
              </section>
            )}

            <section>
              <h3 className={sectionTitle}>Fits any device</h3>
              {data.universal.length === 0 ? <p className="py-2 text-sm text-slate-500">None on file.</p> : renderRows(data.universal)}
            </section>

            <p className="border-t border-slate-100 pt-4 text-xs text-slate-500">
              Using a part takes it out of stock and adds a staff-only note to the ticket.{' '}
              <Link
                to={data.asset.modelId ? `/parts?modelId=${data.asset.modelId}` : '/parts'}
                className="font-medium text-brand-700 hover:underline"
              >
                Manage parts
              </Link>
            </p>
          </div>
        )
      )}
      {data && (
        <PartFormModal
          open={adding}
          onClose={() => setAdding(false)}
          defaults={{ kind: 'REPAIR_PART', compatibleModelIds: data.asset.modelId ? [data.asset.modelId] : [] }}
        />
      )}
    </Modal>
  );
}
